// topology.js — Topology module: Hopfion + Chaos panels.
// Mirrors the plasma-research pattern: WASM init, sub-tab switching,
// per-panel run handlers, custom-paint canvases (no Chart.js dependency).

import init, {
  run_topology_hopfion,
  run_topology_chaos_bifurcation,
  run_topology_chaos_lyapunov,
  run_topology_spin
} from './engines.js';
import { getLicense, hasFullAccess } from './auth.js';
import { usageLog } from './api.js';

let ready = false;
const _data = {};

// ============================================================================
// 3D viewport adapter
// Maps this module's outputs to a window.gdbs3D payload { points, values,
// lines, title } and publishes it to window.__gdbs3dPayload every run. Guarded
// so nothing breaks when the shared viewport is absent. Touches no physics.
// ============================================================================

// Hopfion z=0 slice: an N-by-N orientation field -> one point per cell, z =
// n_z component, coloured by the in-plane angle proxy (atan2(ny, nx)).
function payloadFromHopfion(res, title) {
  if (!res || !res.n_grid || !res.slice_z0_nx) return null;
  const n = res.n_grid;
  if (res.slice_z0_nx.length !== n * n) return null;
  const pts = [], values = [];
  for (let iy = 0; iy < n; iy++) for (let ix = 0; ix < n; ix++) {
    const i = iy * n + ix;
    const nx = res.slice_z0_nx[i], ny = res.slice_z0_ny[i], nz = res.slice_z0_nz[i];
    const x = ix / (n - 1) - 0.5;
    const y = iy / (n - 1) - 0.5;
    pts.push(x, y, nz * 0.5);
    values.push(Math.atan2(ny, nx));
  }
  return { points: new Float32Array(pts), values, title };
}

// Bifurcation diagram (r, x) plus Lyapunov exponent lifted into z -> a 3D
// scatter: x = r, y = attractor x, z = local lambda (nearest r sample).
function payloadFromChaos(bif, lya, title) {
  if (!bif || !bif.r_per_point || !bif.x_values) return null;
  const rmin = bif.r_per_point[0];
  const rmax = bif.r_per_point[bif.r_per_point.length - 1];
  const rspan = Math.max(1e-12, rmax - rmin);
  // Build a quick r -> lambda lookup over the Lyapunov grid for the z lift.
  const lr = (lya && lya.r_values) || [];
  const ll = (lya && lya.lambda) || [];
  const lrmin = lr.length ? lr[0] : 0;
  const lrspan = lr.length ? Math.max(1e-12, lr[lr.length - 1] - lr[0]) : 1;
  const lam = (r) => {
    if (!lr.length) return 0;
    let k = Math.round(((r - lrmin) / lrspan) * (lr.length - 1));
    if (k < 0) k = 0; if (k >= ll.length) k = ll.length - 1;
    return ll[k] || 0;
  };
  const pts = [], values = [];
  for (let i = 0; i < bif.x_values.length; i++) {
    const r = bif.r_per_point[i];
    const x = (r - rmin) / rspan - 0.5;
    const y = bif.x_values[i] - 0.5;
    const l = lam(r);
    pts.push(x, y, Math.max(-3, Math.min(2, l)) / 5);
    values.push(l);
  }
  return { points: new Float32Array(pts), values, title };
}

// Spin / a_e cascade: the result carries a closed-form model
//   a_e(x) = c1 x + c2 x^2 + c3 x^3 + c4 x^4 + c5 x^5,  x = alpha/pi.
// A handful of scalars on their own, but the model is a genuine function of
// 1/alpha, so we evaluate it over a DENSE 1/alpha sweep crossed with loop
// order n: x = 1/alpha sample, y = loop order n (1..5), z = partial-sum a_e
// through order n, coloured by the per-order term magnitude. This renders the
// cascade convergence as a surface (hundreds of points) instead of a few bars.
// The actual computed (alpha_inv, a_e_substrate) point is appended as a marker.
function payloadFromSpin(res, title) {
  if (!res || typeof res.a_e_substrate !== 'number') return null;
  const cs = [res.c1_schwinger, res.c2_petermann, res.c3_laporta_remiddi,
              res.c4_ahkn, res.c5_kinoshita];
  const orders = res.c5_kinoshita ? 5 : 4;   // c5 may be 0 when 5-loop excluded
  const pi = Math.PI;
  const ainv0 = res.alpha_inv || 137.035999084;
  // Dense sweep of 1/alpha around the supplied value (physically the running
  // coupling spans this neighbourhood); ~240 samples x `orders` orders.
  const N = 240;
  const span = 30.0;                          // 1/alpha window half-width
  const aMin = Math.max(1.0, ainv0 - span);
  const aMax = ainv0 + span;
  const pts = [], values = [];
  for (let s = 0; s < N; s++) {
    const ainv = aMin + (aMax - aMin) * (s / (N - 1));
    const x = (1.0 / ainv) / pi;
    let partial = 0.0;
    for (let n = 1; n <= orders; n++) {
      const term = cs[n - 1] * Math.pow(x, n);
      partial += term;
      // x = 1/alpha, y = loop order, z = partial-sum a_e through this order.
      pts.push(ainv, n, partial);
      // Colour by signed magnitude of this order's contribution.
      values.push(Math.sign(term) * Math.log10(Math.abs(term) + 1e-30));
    }
  }
  // Mark the actual substrate result at the highest order.
  pts.push(ainv0, orders, res.a_e_substrate);
  values.push(Math.sign(res.a_e_substrate) * Math.log10(Math.abs(res.a_e_substrate) + 1e-30));
  return { points: new Float32Array(pts), values, title };
}

function publish3D(payload) {
  if (payload) window.__gdbs3dPayload = payload;
}

// Add a small "View in 3D" button beside a results summary, idempotent.
function add3DButton(summaryId, getPayload) {
  if (!window.gdbs3D) return;
  const sum = document.getElementById(summaryId);
  const host = sum ? sum.parentElement : null;
  if (!host) return;
  let btn = host.querySelector('.top-view3d-btn');
  if (!btn) {
    btn = document.createElement('button');
    btn.className = 'top-view3d-btn';
    btn.type = 'button';
    btn.textContent = 'View in 3D';
    btn.style.cssText = 'margin-left:8px;padding:4px 12px;background:#0b2030;color:#4fc3f7;border:1px solid #1e3a5f;border-radius:4px;cursor:pointer;font-size:11px;';
    host.appendChild(btn);
  }
  btn.onclick = () => {
    const p = getPayload();
    publish3D(p);
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  };
}

export async function initTopology() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('topology WASM init failed:', e);
  }
}

// ============================================================================
// Sub-tab switching
// ============================================================================

function bindSubtabs() {
  document.querySelectorAll('.toptab').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.toptab').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('#view-topology .materials-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      const panel = document.getElementById('topp-' + btn.dataset.top);
      if (panel) panel.classList.add('active');
    });
  });
}

// ============================================================================
// Hopfion panel
// ============================================================================

async function runHopfion() {
  const sum = document.getElementById('hop-summary');
  if (!ready) { if (sum) { sum.textContent = 'WASM loading… click again in a moment'; sum.style.color = '#fbbf24'; } return; }
  if (typeof run_topology_hopfion !== 'function') {
    console.error('Hopfion: run_topology_hopfion missing from wasm bundle (stale pkg?)');
    if (sum) { sum.textContent = 'Hopfion engine not in this build - hard-refresh (Ctrl+F5) to reload the wasm bundle'; sum.style.color = '#fca5a5'; }
    return;
  }
  window.gdbsShowLoading && window.gdbsShowLoading('Relaxing hopfion...', 'topological energy descent');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const req = {
      n: parseInt(document.getElementById('hop-n').value) || 32,
      q_h: parseInt(document.getElementById('hop-qh').value) || 1,
      lambda: parseFloat(document.getElementById('hop-lambda').value) || 0.5,
      steps: parseInt(document.getElementById('hop-steps').value) || 80,
      dt: parseFloat(document.getElementById('hop-dt').value) || 0.05,
      box_half: parseFloat(document.getElementById('hop-l').value) || 3.0,
    };
    const t0 = performance.now();
    const res = JSON.parse(run_topology_hopfion(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.hopfion = res;
    usageLog.compute('topology', 'hopfion', dt, { q_h: req.q_h, n: req.n });
    window.gdbsToastSuccess?.({
      title: 'Hopfion relaxed',
      body: `Q_H=${res.q_h}, E_init → E_final = ${res.e_initial.toExponential(2)} → ${res.e_final.toExponential(2)} (${dt.toFixed(0)} ms)`,
    });
    if (sum) {
      sum.style.color = '#94a3b8';
      sum.textContent = dt.toFixed(0) + ' ms · Q_H=' + res.q_h
        + ' · E_init=' + res.e_initial.toExponential(3)
        + ' · E_final=' + res.e_final.toExponential(3)
        + ' · drop=' + ((1 - res.e_final / res.e_initial) * 100).toFixed(1) + '%';
    }
    paintHopEnergy(res);
    paintHopSlice(res);
    paintHopReadout(res);
    publish3D(payloadFromHopfion(res, 'Hopfion n̂(x,y) z=0 slice'));
    add3DButton('hop-summary', () => payloadFromHopfion(_data.hopfion, 'Hopfion n̂(x,y) z=0 slice'));
  } catch (e) {
    console.error('Hopfion error:', e);
    if (sum) { sum.textContent = 'ERROR: ' + (e.message || e); sum.style.color = '#fca5a5'; }
  } finally {
    window.gdbsHideLoading && window.gdbsHideLoading();
  }
}

function paintHopEnergy(res) {
  const cnv = document.getElementById('c-hop-energy');
  if (!cnv) return;
  const eh = res.e_history;
  if (!eh || eh.length < 2) return;

  // Replace the previous raw-2D paint with Chart.js so the energy
  // descent inherits the global zoom/pan plugin config (wheel-zoom,
  // drag-pan, double-click reset) and flips with the theme bridge.
  const prev = (typeof Chart !== 'undefined') ? Chart.getChart(cnv) : null;
  if (prev) prev.destroy();

  const data = new Array(eh.length);
  for (let i = 0; i < eh.length; i++) data[i] = { x: i, y: eh[i] };
  const c = chartThemeColors();

  new Chart(cnv.getContext('2d'), {
    type: 'line',
    data: {
      datasets: [{
        label: 'E[n̂]',
        data,
        parsing: false,
        borderColor: '#fbbf24',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx) => `step ${ctx.parsed.x}  E=${ctx.parsed.y.toExponential(3)}`,
          },
        },
        zoom: ZOOM_PAN_OPTS,
      },
      scales: {
        x: {
          type: 'linear', min: 0, max: eh.length - 1,
          title:  { display: true, text: 'descent step', color: c.dim, font: { size: 10 } },
          ticks:  { color: c.dim, font: { size: 10 } },
          grid:   { color: c.grid + '33' },
          border: { color: c.grid },
        },
        y: {
          title:  { display: true, text: 'E', color: c.dim, font: { size: 10 } },
          ticks:  { color: c.dim, font: { size: 10 } },
          grid:   { color: c.grid + '33' },
          border: { color: c.grid },
        },
      },
    },
  });
}

function paintHopSlice(res) {
  const cnv = document.getElementById('c-hop-slice');
  if (!cnv) return;
  // Theme-aware background — pull --code-bg so the slice plate matches
  // the surrounding panel surface in both light and dark modes instead
  // of the previous hardcoded #080d1a deep navy.
  const cs = getComputedStyle(document.documentElement);
  const bg = cs.getPropertyValue('--code-bg').trim() || '#080d1a';
  const border = cs.getPropertyValue('--border').trim() || 'rgba(148,163,184,0.3)';

  const w = cnv.clientWidth || 240, h = cnv.clientHeight || 240;
  cnv.width = w; cnv.height = h;
  const ctx = cnv.getContext('2d');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
  const n = res.n_grid;
  if (!res.slice_z0_nx || res.slice_z0_nx.length !== n * n) return;
  const cellW = w / n, cellH = h / n;
  for (let iy = 0; iy < n; iy++) {
    for (let ix = 0; ix < n; ix++) {
      const i = iy * n + ix;
      const nx = res.slice_z0_nx[i];
      const ny = res.slice_z0_ny[i];
      const nz = res.slice_z0_nz[i];
      const theta = Math.atan2(ny, nx);
      const hue = ((theta + Math.PI) / (2 * Math.PI)) * 360;
      const light = 50 + nz * 30;
      ctx.fillStyle = 'hsl(' + hue.toFixed(0) + ',70%,' + light.toFixed(0) + '%)';
      ctx.fillRect(ix * cellW, h - (iy + 1) * cellH, cellW + 0.5, cellH + 0.5);
    }
  }
  ctx.strokeStyle = border;
  ctx.strokeRect(0, 0, w, h);
}

function paintHopReadout(res) {
  const el = document.getElementById('hop-readout');
  if (!el) return;
  const vkColor = res.vaks_satisfied ? '#6ee7b7' : '#fca5a5';
  const vkVerdict = res.vaks_satisfied ? 'satisfied' : 'violated';
  el.innerHTML =
    '<strong>Q<sub>H</sub></strong> = ' + res.q_h
    + ' &middot; <strong>N</strong> = ' + res.n_grid + '&#179;'
    + ' &middot; <strong>L</strong> = ' + res.box_half.toFixed(2)
    + ' &middot; <strong>&lambda;</strong> = ' + res.lambda.toFixed(3)
    + ' &middot; steps run: ' + res.steps_run + '<br>'
    + '<strong>E<sub>final</sub></strong> = ' + res.e_final.toExponential(4)
    + ' (&sigma; term: ' + res.e_sigma_final.toExponential(3)
    + ', Faddeev term: ' + res.e_faddeev_final.toExponential(3) + ')<br>'
    + 'Vakulenko-Kapitanski bound c&middot;|Q<sub>H</sub>|<sup>3/4</sup> = '
    + res.vaks_bound.toExponential(3)
    + ' &mdash; <span style="color:' + vkColor + '">' + vkVerdict + '</span><br>'
    + 'n<sub>z</sub>&asymp;0 preimage proxy: ' + res.iso_nz0_count
    + ' grid cells (scales with |Q<sub>H</sub>|)';
}

// ============================================================================
// Chaos panel
// ============================================================================

async function runChaos() {
  const sum = document.getElementById('cha-summary');
  if (!ready) { if (sum) { sum.textContent = 'WASM loading… click again in a moment'; sum.style.color = '#fbbf24'; } return; }
  const rmin = parseFloat(document.getElementById('cha-rmin').value) || 2.5;
  const rmax = parseFloat(document.getElementById('cha-rmax').value) || 4.0;
  const nr = parseInt(document.getElementById('cha-nr').value) || 600;
  const x0 = parseFloat(document.getElementById('cha-x0').value) || 0.5;
  const bifReq = { r_min: rmin, r_max: rmax, n_r: nr, n_warmup: 500, n_sample: 100, x0 };
  const lyaReq = { r_min: rmin, r_max: rmax, n_r: Math.min(nr * 2, 1600), n_warmup: 1000, n_iter: 2000, x0 };

  window.gdbsShowLoading && window.gdbsShowLoading('Running chaos analysis...', 'bifurcation + Lyapunov sweep');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const t0 = performance.now();
    const bif = JSON.parse(run_topology_chaos_bifurcation(JSON.stringify(bifReq)));
    const lya = JSON.parse(run_topology_chaos_lyapunov(JSON.stringify(lyaReq)));
    const dt = performance.now() - t0;
    _data.chaos = { bif, lya };
    usageLog.compute('topology', 'chaos', dt, { r_min: bifReq.r_min, r_max: bifReq.r_max });
    window.gdbsToastSuccess?.({
      title: 'Chaos analysis',
      body: `Feigenbaum δ ≈ ${bif.feigenbaum_delta.toFixed(3)} (canonical 4.6692), chaos fraction ${(lya.chaos_fraction * 100).toFixed(1)}%`,
    });
    if (sum) {
      sum.style.color = '#94a3b8';
      sum.textContent = dt.toFixed(0) + ' ms · Feigenbaum δ ≈ '
        + bif.feigenbaum_delta.toFixed(3)
        + ' (canonical 4.6692) · chaos fraction = '
        + (lya.chaos_fraction * 100).toFixed(1) + '%';
    }
    paintBifurcation(bif);
    paintLyapunov(lya);
    paintChaosReadout(bif, lya);
    publish3D(payloadFromChaos(bif, lya, 'Logistic map: r, x, λ'));
    add3DButton('cha-summary', () => payloadFromChaos(_data.chaos?.bif, _data.chaos?.lya, 'Logistic map: r, x, λ'));
  } finally {
    window.gdbsHideLoading && window.gdbsHideLoading();
  }
}

// Theme-aware grid/text colors pulled from CSS custom properties so the
// chart axes flip with dark/light mode automatically.
// CSS custom-property aliases (--text-bright → var(--text-primary)) are
// sometimes returned by getPropertyValue as the literal "var(...)"
// string rather than a resolved color, which Chart.js then rejects.
// Resolve through a hidden probe element so we always get an rgb()/oklch()
// computed color.
let _probeEl = null;
function _resolveColor(varExpr) {
  if (!_probeEl) {
    _probeEl = document.createElement('span');
    _probeEl.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;left:-9999px;';
    document.body.appendChild(_probeEl);
  }
  _probeEl.style.color = '';
  _probeEl.style.color = varExpr;
  return getComputedStyle(_probeEl).color || '';
}
function chartThemeColors() {
  return {
    text:   _resolveColor('var(--text-primary, var(--text, #cbd5e1))'),
    dim:    _resolveColor('var(--text-secondary, var(--text-dim, #94a3b8))'),
    grid:   _resolveColor('var(--border, #334155)'),
    accent: _resolveColor('var(--accent, #4fc3f7)'),
  };
}

// Explicit zoom/pan plugin options for any chart in this module — global
// `Chart.defaults.plugins.zoom` set in index.html doesn't always reach a
// per-chart options.plugins object that already declares legend/tooltip
// keys (Chart.js sometimes shallow-merges plugins). Inlining the config
// here guarantees wheel-zoom + drag-pan + double-click-reset works.
const ZOOM_PAN_OPTS = {
  zoom: {
    wheel: { enabled: true },
    pinch: { enabled: true },
    drag:  { enabled: false },
    mode:  'xy',
  },
  pan: {
    enabled:   true,
    mode:      'xy',
    threshold: 5,
  },
  limits: {
    x: { min: 'original', max: 'original' },
    y: { min: 'original', max: 'original' },
  },
};

function paintBifurcation(bif) {
  const cnv = document.getElementById('c-cha-bif');
  if (!cnv) return;
  // Replace the previous raw-2D paint with Chart.js so the bifurcation
  // diagram inherits the global zoom/pan plugin config (wheel-zoom,
  // drag-pan, double-click reset) and the theme bridge's color tokens.
  const prev = (typeof Chart !== 'undefined') ? Chart.getChart(cnv) : null;
  if (prev) prev.destroy();

  const data = new Array(bif.x_values.length);
  for (let i = 0; i < bif.x_values.length; i++) {
    data[i] = { x: bif.r_per_point[i], y: bif.x_values[i] };
  }
  const c = chartThemeColors();
  const rmin = bif.r_values[0], rmax = bif.r_values[bif.r_values.length - 1];

  // Onset markers — vertical line per onset r-value, drawn as a separate
  // dataset with two points (r, 0) and (r, 1). Cheaper than pulling in
  // chartjs-plugin-annotation just for two reference lines.
  const onsetDatasets = (bif.onsets || [])
    .filter(r => r >= rmin && r <= rmax)
    .map((r, idx) => ({
      label: `onset r=${r.toFixed(3)}`,
      data: [{ x: r, y: 0 }, { x: r, y: 1 }],
      parsing: false,
      borderColor: 'rgba(251,191,36,0.55)',
      borderDash: [3, 3],
      borderWidth: 1,
      pointRadius: 0,
      showLine: true,
      type: 'line',
    }));

  new Chart(cnv.getContext('2d'), {
    type: 'scatter',
    data: {
      datasets: [
        {
          label: 'attractor',
          data,
          parsing: false,
          pointRadius: 0.8,
          pointHoverRadius: 2,
          pointBackgroundColor: c.accent,
          pointBorderWidth: 0,
          showLine: false,
        },
        ...onsetDatasets,
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx) => ctx.datasetIndex === 0
              ? `r=${ctx.parsed.x.toFixed(4)}  x=${ctx.parsed.y.toFixed(4)}`
              : ctx.dataset.label,
          },
        },
        zoom: ZOOM_PAN_OPTS,
      },
      scales: {
        x: {
          type: 'linear', min: rmin, max: rmax,
          title:  { display: true, text: 'r', color: c.dim, font: { size: 10 } },
          ticks:  { color: c.dim, font: { size: 10 } },
          grid:   { color: c.grid + '33' },
          border: { color: c.grid },
        },
        y: {
          min: 0, max: 1,
          title:  { display: true, text: 'x', color: c.dim, font: { size: 10 } },
          ticks:  { color: c.dim, font: { size: 10 } },
          grid:   { color: c.grid + '33' },
          border: { color: c.grid },
        },
      },
    },
  });
}

function paintLyapunov(lya) {
  const cnv = document.getElementById('c-cha-lya');
  if (!cnv) return;
  const prev = (typeof Chart !== 'undefined') ? Chart.getChart(cnv) : null;
  if (prev) prev.destroy();

  // Build a single dataset with {x: r, y: λ} pairs; segment.borderColor
  // colors each segment orange when λ > 0 (chaos) and green when stable.
  const data = new Array(lya.r_values.length);
  for (let i = 0; i < lya.r_values.length; i++) {
    data[i] = { x: lya.r_values[i], y: lya.lambda[i] };
  }
  const c = chartThemeColors();
  const rmin = lya.r_values[0], rmax = lya.r_values[lya.r_values.length - 1];

  new Chart(cnv.getContext('2d'), {
    type: 'line',
    data: {
      datasets: [
        {
          label: 'λ(r)',
          data,
          parsing: false,
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0,
          segment: {
            borderColor: (s) => s.p1.parsed.y > 0 ? '#fbbf24' : '#86efac',
          },
          spanGaps: true,
        },
        {
          label: 'λ = 0 (chaos threshold)',
          data: [{ x: rmin, y: 0 }, { x: rmax, y: 0 }],
          parsing: false,
          borderColor: 'rgba(252,165,165,0.55)',
          borderWidth: 1,
          pointRadius: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      parsing: false,
      normalized: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx) => ctx.datasetIndex === 0
              ? `r=${ctx.parsed.x.toFixed(4)}  λ=${ctx.parsed.y.toFixed(4)}`
              : ctx.dataset.label,
          },
        },
        zoom: ZOOM_PAN_OPTS,
      },
      scales: {
        x: {
          type: 'linear', min: rmin, max: rmax,
          title:  { display: true, text: 'r', color: c.dim, font: { size: 10 } },
          ticks:  { color: c.dim, font: { size: 10 } },
          grid:   { color: c.grid + '33' },
          border: { color: c.grid },
        },
        y: {
          min: -3, max: 2,
          title:  { display: true, text: 'λ', color: c.dim, font: { size: 10 } },
          ticks:  { color: c.dim, font: { size: 10 } },
          grid:   { color: c.grid + '33' },
          border: { color: c.grid },
        },
      },
    },
  });
}

function paintChaosReadout(bif, lya) {
  const el = document.getElementById('cha-readout');
  if (!el) return;
  // Sample λ at r=4 specifically if visible.
  let l4 = null;
  for (let i = 0; i < lya.r_values.length; i++) {
    if (Math.abs(lya.r_values[i] - 4.0) < 0.005) { l4 = lya.lambda[i]; break; }
  }
  if (l4 === null && lya.r_values.length) l4 = lya.lambda[lya.lambda.length - 1];
  el.innerHTML =
    '<strong>Bifurcation</strong>: ' + bif.x_values.length + ' samples across '
    + bif.r_values.length + ' r values · onsets {'
    + bif.onsets.map(r => r.toFixed(3)).join(', ') + '} · '
    + bif.elapsed_ms.toFixed(0) + ' ms<br>'
    + '<strong>Lyapunov</strong>: λ(r≈4) = ' + (l4 == null ? '-' : l4.toFixed(4))
    + ' (canonical ln 2 = ' + Math.log(2).toFixed(4) + ') · '
    + 'positive-λ fraction = ' + (lya.chaos_fraction * 100).toFixed(1) + '% · '
    + lya.elapsed_ms.toFixed(0) + ' ms<br>'
    + '<strong>Feigenbaum δ</strong> (5-point estimate) = '
    + bif.feigenbaum_delta.toFixed(4) + ' vs canonical 4.6692';
}

// ============================================================================
// Bootstrap
// ============================================================================

// Expose gating so app.js can call it after license refresh, AND invoke
// immediately on module load so the view is not blank if app.js's gating
// block runs before topology.js's module has loaded (ES-modules race).
function applyTopologyGating() {
  const license = getLicense();
  const tier = license?.tier || 'trial';
  const has = hasFullAccess(tier)
    || license?.modules?.includes('topology')
    || (tier === 'trial' && license?.isActive);
  const g = document.getElementById('top-gate');
  const c = document.getElementById('top-content');
  if (g && c) {
    if (has) { g.classList.add('hidden'); c.classList.remove('hidden'); }
    else { g.classList.remove('hidden'); c.classList.add('hidden'); }
  }
}
window.gdbsApplyTopologyGating = applyTopologyGating;
// Eagerly apply at the earliest opportunity, even before WASM init finishes.
if (document.readyState !== 'loading') {
  applyTopologyGating();
} else {
  document.addEventListener('DOMContentLoaded', applyTopologyGating);
}
// Also re-run on every Topology nav click (auth may have just landed).
document.addEventListener('click', (ev) => {
  const btn = ev.target?.closest?.('.nav-item[data-view="topology"]');
  if (btn) applyTopologyGating();
});

async function runSpin() {
  const sum = document.getElementById('sp-summary');
  if (!ready) {
    if (sum) { sum.textContent = 'WASM not ready yet — hard-refresh (Ctrl+F5) to reload bundle'; sum.style.color = '#fca5a5'; }
    console.warn('Spin: WASM not ready');
    return;
  }
  window.gdbsShowLoading && window.gdbsShowLoading('Computing spin...', 'Pauli algebra + a_e cascade');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const ainv = parseFloat(document.getElementById('sp-ainv').value) || 137.035999084;
    const c5 = document.getElementById('sp-c5').checked;
    const req = { alpha_inv: ainv, include_c5: c5 };
    const t0 = performance.now();
    const res = JSON.parse(run_topology_spin(JSON.stringify(req)));
    const dt = performance.now() - t0;
    _data.spin = res;
    usageLog.compute('topology', 'spin', dt, { alpha_inv: req.alpha_inv, c5: req.include_c5 });
    window.gdbsToastSuccess?.({
      title: 'Spin compute',
      body: `a_e matches CODATA at ${(res.a_e_relative_error * 1e6).toFixed(3)} ppm (${dt.toFixed(0)} ms)`,
    });
    if (sum) { sum.style.color = '#94a3b8'; sum.textContent = dt.toFixed(1) + ' ms · a_e error vs CODATA = ' + (res.a_e_relative_error * 1e6).toFixed(2) + ' ppm'; }
    paintPauli(res);
    paintResiduals(res);
    paintCascade(res);
    publish3D(payloadFromSpin(res, 'QED a_e cascade: 1/alpha x loop order'));
    add3DButton('sp-summary', () => payloadFromSpin(_data.spin, 'QED a_e cascade: 1/alpha x loop order'));
  } catch (e) {
    console.error('Spin error:', e);
    if (sum) { sum.textContent = 'ERROR: ' + (e.message || e); sum.style.color = '#fca5a5'; }
  } finally {
    window.gdbsHideLoading && window.gdbsHideLoading();
  }
}

function fmtM(m) {
  const lines = [];
  for (let i = 0; i < 2; i++) {
    const row = [];
    for (let j = 0; j < 2; j++) {
      const re = m.re[i][j].toFixed(0);
      const im = m.im[i][j];
      let cell;
      if (Math.abs(m.im[i][j]) < 1e-12) cell = re;
      else if (Math.abs(m.re[i][j]) < 1e-12) cell = (im > 0 ? '+' : '') + im.toFixed(0) + 'i';
      else cell = re + (im > 0 ? '+' : '') + im.toFixed(0) + 'i';
      row.push(cell.padStart(4));
    }
    lines.push('  [ ' + row.join(', ') + ' ]');
  }
  return lines.join('\n');
}

function paintPauli(res) {
  const el = document.getElementById('sp-matrices');
  if (!el) return;
  el.textContent =
    'σ_x =\n' + fmtM(res.sigma_x) + '\n\n' +
    'σ_y =\n' + fmtM(res.sigma_y) + '\n\n' +
    'σ_z =\n' + fmtM(res.sigma_z);
}

function paintResiduals(res) {
  const tbody_html =
    '<thead><tr><th>Identity</th><th>Residual</th></tr></thead><tbody>' +
    rowR('||[σ_x, σ_y] − 2iσ_z||', res.commutator_xy_minus_2iz) +
    rowR('||[σ_y, σ_z] − 2iσ_x||', res.commutator_yz_minus_2ix) +
    rowR('||[σ_z, σ_x] − 2iσ_y||', res.commutator_zx_minus_2iy) +
    rowR('||{σ_x, σ_x} − 2I||',    res.anticom_xx_minus_2I) +
    rowR('||{σ_x, σ_y}||',         res.anticom_xy_residual) +
    rowR('||{σ_x, σ_z}||',         res.anticom_xz_residual) +
    rowR('||{σ_y, σ_z}||',         res.anticom_yz_residual) +
    rowD('det(σ_x), det(σ_y), det(σ_z)', [res.det_x, res.det_y, res.det_z]) +
    rowD('tr(σ_x), tr(σ_y), tr(σ_z)', [res.trace_x, res.trace_y, res.trace_z]) +
    '</tbody>';
  document.getElementById('sp-residuals').innerHTML = tbody_html;
}

function rowR(name, val) {
  const pass = Math.abs(val) < 1e-12;
  const color = pass ? '#6ee7b7' : '#fca5a5';
  // The Pauli matrices are built from exact integer entries, so the SU(2)
  // commutator/anticommutator residuals close to identically zero (not just
  // near the f64 floor). Render that as an explicit "0 (exact)" pass rather
  // than a string of zeros that reads as a broken column.
  const shown = (val === 0) ? '0 (exact)' : val.toExponential(3);
  return '<tr><td>' + name + '</td><td style="color:' + color + ';font-family:monospace;">' + shown + '</td></tr>';
}
function rowD(name, vals) {
  return '<tr><td>' + name + '</td><td style="font-family:monospace;">'
    + vals.map(v => v.toFixed(3)).join(', ') + '</td></tr>';
}

function paintCascade(res) {
  const el = document.getElementById('sp-cascade');
  if (!el) return;

  /* F31 correction. This panel used to print the c4/c5 ansatz denominators in
     bold with substrate framing and no hedge, which read as "the substrate
     establishes these coefficients through integer structure". That claim is
     retracted: 234375 = 3·5⁷ and 343 = 7³ are the COMMON DENOMINATORS of
     closed forms that were FOUND BY A SEARCH targeting the published values,
     and the coefficients themselves are irrational, so no integrality property
     holds. The engine now states this in-band (coefficient_provenance) and
     ships each coefficient's residual against its published reference
     (c2..c5_residual_vs_published); this painter surfaces both so every row
     reads as value-plus-limit instead of value-plus-implied-derivation. */

  /* Absolute residual |evaluated - published| for one coefficient row. An
     engine build predating the residual fields yields undefined here; say so
     explicitly rather than throwing or silently omitting the limit. */
  function residualNote(val) {
    if (typeof val === 'number' && isFinite(val)) {
      return ' · residual vs published ' + val.toExponential(2);
    } else {
      return ' · residual vs published not reported by this engine build';
    }
  }

  /* The provenance string is interpolated into innerHTML, so escape it even
     though today it is a fixed engine-side sentence: this painter must not
     become an injection point if the field ever carries anything else. */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  let provenanceText;
  if (typeof res.coefficient_provenance === 'string' && res.coefficient_provenance.length > 0) {
    provenanceText = escapeHtml(res.coefficient_provenance);
  } else {
    /* Honest gap, not a fabricated stand-in: without the in-band string we
       cannot assert where the coefficient values came from. */
    provenanceText = 'Coefficient provenance was not reported by this engine build.';
  }

  /* When "include c5" is unchecked the engine excludes the 5-loop term from
     the series and reports c5_kinoshita = 0. Printing that zero next to the
     real evaluated-c5 residual would look like a broken evaluation, so state
     the exclusion instead of the number. */
  let c5Line;
  if (res.c5_kinoshita === 0) {
    c5Line = '&nbsp;&nbsp;c<sub>5</sub> (Kinoshita, 2017): excluded from this run (include-c5 unchecked)<br>';
  } else {
    c5Line = '&nbsp;&nbsp;c<sub>5</sub> (Kinoshita, 2017) = ' + res.c5_kinoshita.toFixed(6) + residualNote(res.c5_residual_vs_published) + '<br>';
  }

  el.innerHTML =
    '<strong>QED a<sub>e</sub> cascade on substrate</strong><br>' +
    '&nbsp;&nbsp;c<sub>1</sub> (Schwinger, 1948) = ' + res.c1_schwinger + '<br>' +
    '&nbsp;&nbsp;c<sub>2</sub> (Petermann, 1957) = ' + res.c2_petermann.toFixed(9) + residualNote(res.c2_residual_vs_published) + '<br>' +
    '&nbsp;&nbsp;c<sub>3</sub> (Laporta-Remiddi, 1996) = ' + res.c3_laporta_remiddi.toFixed(6) + residualNote(res.c3_residual_vs_published) + '<br>' +
    '&nbsp;&nbsp;c<sub>4</sub> (AHKN, 2012) = ' + res.c4_ahkn.toFixed(6) + residualNote(res.c4_residual_vs_published) + '<br>' +
    c5Line +
    '&nbsp;&nbsp;1/α = ' + res.alpha_inv.toFixed(9) + '<br>' +
    '<br>' +
    'a<sub>e</sub> (substrate) = ' + res.a_e_substrate.toExponential(8) + '<br>' +
    'a<sub>e</sub> (CODATA 2022) = ' + res.a_e_codata.toExponential(8) + '<br>' +
    'relative error = ' + (res.a_e_relative_error * 1e6).toFixed(3) + ' ppm<br>' +
    '<br>' +
    '<em>' + provenanceText + '</em><br>' +
    '<em>Note on the c<sub>4</sub>/c<sub>5</sub> closed forms: they share ansatz common denominators ' +
    res.c4_denom + ' = 3·5⁷ and ' + res.c5_denom + ' = 7³. The coefficients themselves are irrational, ' +
    'so no integrality property holds, and the forms were found by a search targeting the published ' +
    'values - reproducing those values is guaranteed by construction and is not a derivation.</em>';
}

// Wire button click handlers immediately at module load so they exist
// even if a user clicks before async WASM init completes. Each handler
// checks `ready` and surfaces a friendly message if it's still loading.
function wireButtonsEagerly() {
  bindSubtabs();
  document.getElementById('btn-hopfion-run')?.addEventListener('click', runHopfion);
  document.getElementById('btn-chaos-run')?.addEventListener('click', runChaos);
  document.getElementById('btn-spin-run')?.addEventListener('click', runSpin);
}
if (document.readyState !== 'loading') {
  wireButtonsEagerly();
} else {
  document.addEventListener('DOMContentLoaded', wireButtonsEagerly);
}

async function bootstrap() {
  await initTopology();
  window.gdbsApplyTopologyGating();

  // Auto-run the hopfion on first open of the Topology view so users see
  // something interesting immediately.
  const navBtn = document.querySelector('.nav-item[data-view="topology"]');
  if (navBtn) {
    let ran = false;
    navBtn.addEventListener('click', () => {
      if (!ran) {
        ran = true;
        setTimeout(() => runHopfion(), 60);
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

export { _data };
